// src/pages/DashboardPage.js

import React, { useEffect, useState } from "react";
import SubmissionsTable from "../components/Dashboard/SubmissionsTable";
import ConfirmationModal from "../components/ConfirmationModal"; // Import the modal
import { fetchSubmissions, deleteSubmission } from "../utils/api"; // Import deleteSubmission
import { isTokenExpired, refreshToken, logout } from "../utils/auth";

const DashboardPage = ({ onLogout }) => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [filterQuery, setFilterQuery] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' or 'desc'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for advanced filtering modal
  const [showFilterModal, setShowFilterModal] = useState(false);

  // State for confirmation modal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);

  // Mapping object for field display names
  const fieldDisplayNames = {
    fullName: "Full Name",
    email: "Email Address",
    phoneNumber: "Phone Number",
    desiredMoveInDate: "Desired Move-In Date",
    lengthOfSobriety: "Length of Sobriety",
    treatmentCenter: "Coming from Treatment Center",
    treatmentCenterName: "Treatment Center Name",
    caseManagerName: "Case Manager Name",
    caseManagerContact: "Case Manager Number",
    waitingList: "Waiting List",
  };

  // fieldFilters will be an object with keys as field names and values as arrays of selected filters.
  const [fieldFilters, setFieldFilters] = useState({
    fullName: [],
    email: [],
    phoneNumber: [],
    desiredMoveInDate: [],
    lengthOfSobriety: [],
    treatmentCenter: [],
    treatmentCenterName: [],
    caseManagerName: [],
    caseManagerContact: [],
    waitingList: [],
  });

  const handleLogout = () => {
    logout();
    onLogout();
  };

  const fetchData = async () => {
    try {
      let accessToken = localStorage.getItem("accessToken");
      if (!accessToken || isTokenExpired(accessToken)) {
        console.log("Token expired, refreshing...");
        accessToken = await refreshToken();
      }

      const data = await fetchSubmissions();
      // Sort data by timestamp descending
      data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSubmissions(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching submissions:", err);
      setError(err.message || "An error occurred while fetching submissions.");
      if (err.message === "Unauthorized") {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilterQuery(e.target.value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // If clicking the same field, toggle the sort order
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Get unique values for dropdowns/checkboxes
  const getUniqueValuesForField = (field) => {
    const values = submissions.map((sub) => sub[field] || "").filter((v) => v.trim() !== "");
    return Array.from(new Set(values));
  };

  const toggleFilterModal = () => {
    setShowFilterModal((prev) => !prev);
  };

  const handleFieldFilterChange = (field, value) => {
    setFieldFilters((prevFilters) => {
      const currentValues = prevFilters[field] || [];
      if (currentValues.includes(value)) {
        // Remove it
        return {
          ...prevFilters,
          [field]: currentValues.filter((v) => v !== value),
        };
      } else {
        // Add it
        return {
          ...prevFilters,
          [field]: [...currentValues, value],
        };
      }
    });
  };

  const applyFilters = () => {
    toggleFilterModal();
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = submissions.filter((submission) => {
      // Check text filter
      const valuesToCheck = [
        submission.fullName || "",
        submission.email || "",
        submission.timestamp ? new Date(submission.timestamp).toLocaleString() : "",
        submission.phoneNumber || "",
        submission.desiredMoveInDate || "",
        submission.lengthOfSobriety || "",
        submission.treatmentCenter || "",
        submission.treatmentCenterName || "",
        submission.caseManagerName || "",
        submission.caseManagerContact || "",
        submission.waitingList || "",
      ];

      const query = filterQuery.toLowerCase();
      const textFilterPass = valuesToCheck.some((val) => val.toLowerCase().includes(query));
      if (!textFilterPass) return false;

      // Check field-based filters
      for (const field of Object.keys(fieldFilters)) {
        const selectedValues = fieldFilters[field];
        if (selectedValues.length > 0) {
          const subValue = (submission[field] || "").toString();
          if (!selectedValues.includes(subValue)) {
            return false;
          }
        }
      }

      return true;
    });

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        // For date (timestamp), convert to numeric value
        if (sortField === "timestamp") {
          valA = new Date(valA).getTime();
          valB = new Date(valB).getTime();
        }

        if (valA == null) valA = "";
        if (valB == null) valB = "";

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredSubmissions(filtered);
  }, [submissions, filterQuery, fieldFilters, sortField, sortOrder]);

  /**
   * Handle the deletion of a submission.
   * This function is now triggered from the Confirmation Modal.
   */
  const confirmDelete = async () => {
    if (!submissionToDelete) return;

    try {
      await deleteSubmission(submissionToDelete);
      // Remove the deleted submission from state
      setSubmissions((prevSubmissions) =>
        prevSubmissions.filter((sub) => sub.submissionId !== submissionToDelete)
      );
      setFilteredSubmissions((prevFiltered) =>
        prevFiltered.filter((sub) => sub.submissionId !== submissionToDelete)
      );
      // Close the modal and reset
      setIsConfirmModalOpen(false);
      setSubmissionToDelete(null);
      // Optionally, show a success message
      alert("Submission deleted successfully.");
    } catch (err) {
      console.error("Error deleting submission:", err);
      alert(`Failed to delete submission: ${err.message}`);
      // Close the modal and reset
      setIsConfirmModalOpen(false);
      setSubmissionToDelete(null);
    }
  };

  /**
   * Handle the initial delete button click to open the confirmation modal.
   * @param {string} submissionId - The ID of the submission to delete.
   */
  const handleDelete = (submissionId) => {
    setSubmissionToDelete(submissionId);
    setIsConfirmModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-8 text-gray-100 relative">
      {/* Logout button in the top right, smaller */}
      <button
        onClick={handleLogout}
        className="bg-red-700 text-white px-2 py-1 rounded shadow hover:bg-red-500 transition duration-300 text-sm absolute top-4 right-4"
      >
        Logout
      </button>

      <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-8">
        <img
          src="/unity_living_logo_white.webp"
          alt="Unity Living Logo"
          className="h-12 w-12 sm:h-20 sm:w-20 rounded-lg"
        />
        {/* Smaller font size for "Submissions Dashboard" */}
        <h1 className="text-xl sm:text-2xl font-extrabold">Submissions Dashboard</h1>
      </div>

      {/* Increase gap between input field and button using space-x-4 */}
      <div className="mb-4 w-full sm:w-auto">
        <div className="flex flex-col sm:flex-row w-full sm:max-w-md items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <input
            type="text"
            placeholder="Filter submissions..."
            value={filterQuery}
            onChange={handleFilterChange}
            className="flex-grow px-2 py-1 sm:px-4 sm:py-2 border border-gray-700 bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base rounded"
          />
          <button
            onClick={toggleFilterModal}
            className="bg-blue-800 text-white px-2 py-1 sm:px-4 sm:py-2 rounded shadow hover:bg-blue-600 transition duration-300 text-sm sm:text-base"
          >
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Conditionally render content inside the container */}
      {loading ? (
        <div className="flex justify-center items-center">
          {/* Spinner Example Using Tailwind CSS */}
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
          <p className="ml-2 text-gray-100">Loading submissions...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col justify-center items-center">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <button
            onClick={handleLogout}
            className="bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700 transition duration-300"
          >
            Go to Login
          </button>
        </div>
      ) : (
        <>
          {filteredSubmissions.length > 0 ? (
            <div className="w-full overflow-x-auto">
              <SubmissionsTable
                submissions={filteredSubmissions}
                onSort={handleSort}
                sortField={sortField}
                sortOrder={sortOrder}
                onDelete={handleDelete} // Pass the delete handler
              />
            </div>
          ) : (
            <p className="text-sm sm:text-lg text-gray-300">No submissions found.</p>
          )}
        </>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-2 sm:p-8 rounded shadow-lg max-w-full w-full sm:max-w-2xl text-gray-100 h-full sm:h-auto overflow-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4">Advanced Filters</h2>
            <p className="text-sm sm:text-base">Select the values you want to filter by for each field:</p>
            <div className="max-h-64 overflow-auto mt-2 sm:mt-4 space-y-4">
              {Object.keys(fieldFilters).map((field) => {
                let uniqueValues = getUniqueValuesForField(field);
                if (uniqueValues.length === 0) return null;
                const hasYes = uniqueValues.includes("yes");
                const hasNo = uniqueValues.includes("no");
                if (hasYes && hasNo) {
                  uniqueValues = [
                    "yes",
                    "no",
                    ...uniqueValues.filter((v) => v !== "yes" && v !== "no"),
                  ];
                }

                return (
                  <div key={field}>
                    <h3 className="font-semibold text-sm sm:text-base">
                      {fieldDisplayNames[field] || field}
                    </h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {uniqueValues.map((value) => {
                        const displayValue = value.charAt(0).toUpperCase() + value.slice(1);
                        return (
                          <label key={value} className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              className="form-checkbox h-4 w-4 sm:h-5 sm:w-5 text-blue-500"
                              checked={fieldFilters[field].includes(value)}
                              onChange={() => handleFieldFilterChange(field, value)}
                            />
                            <span>{displayValue}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 sm:mt-6 flex justify-end space-x-2 sm:space-x-4">
              <button
                onClick={toggleFilterModal}
                className="px-2 py-1 sm:px-4 sm:py-2 bg-gray-600 text-gray-100 rounded hover:bg-gray-700 text-sm sm:text-base"
              >
                Close
              </button>
              <button
                onClick={applyFilters}
                className="px-2 py-1 sm:px-4 sm:py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm sm:text-base"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Deletion */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        title="Confirm Deletion"
        message="Are you sure you want to delete this submission? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => {
          setIsConfirmModalOpen(false);
          setSubmissionToDelete(null);
        }}
      />
    </div>
  );
};

export default DashboardPage;
